"use client";
import React, { useMemo } from "react";
import { DoubleBed, FeedbackStar, LivingRoom } from "@/components-shared/icons";
import { CircleAlert, MapPin } from "lucide-react";
import { Badge } from "./ui/badge";
import { useRouter, useSearchParams } from "next/navigation";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import UnitIcon from "./unit-icon";
import FavoriteBtn from "./favorite-btn";
import { useBoolean } from "usehooks-ts";
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from "@tanstack/react-query";
import { useCities } from "@/lib/cities.data";
import { useLocale, useTranslations } from "next-intl";
import { differenceInDays } from "date-fns";
import { cn, roundTo1Decimal } from "@/lib/utils";
import CircularProgress from "./CircularProgress";
import Image from "next/image";
import swimmingPool from "@/public/images/unit-details-icons/swimming-pool.svg";

const PropCard = ({
  propData,
  image,
  id,
  refetch,
}: {
  propData?: Property;
  image?: string;
  id: string;
  refetch?: (
    options?: (RefetchOptions & RefetchQueryFilters<any>) | undefined
  ) => Promise<QueryObserverResult<any, unknown>>;
}) => {
  const { data: cities } = useCities();
  const router = useRouter();
  const lang = useLocale();
  const t = useTranslations();

  const isFavorite = useBoolean(propData?.isFavorite);
  const isLoading = useBoolean(false);

  const searchParams = useSearchParams();

  const duration = useMemo(() => {
    if (searchParams?.get("from") && searchParams?.get("to")) {
      return differenceInDays(
        new Date(searchParams.get("to")!),
        new Date(searchParams.get("from")!)
      );
    } else {
      return 1;
    }
  }, [searchParams]);

  const query: Record<string, string> = {};

  // Dynamically add params if they exist
  if (searchParams.get("from")) {
    query.checkInDate = searchParams.get("from")!;
  }

  if (searchParams.get("to")) {
    query.checkOutDate = searchParams.get("to")!;
  }

  // Build query string dynamically
  const queryString = new URLSearchParams(query).toString();
  const [err, setErr] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  return (
    <Card
      onClick={() =>
        router.push(`/all-units/${id}/${queryString ? `?${queryString}` : ""}`)
      }
      className="cursor-pointer h-fit p-2 sm:p-4 w-full shadow-card rounded-2xl hover:border-green-400 hover:border hover:scale-[103%] transition-all "
    >
      <CardHeader>
        <CardTitle className="relative">
          {Number(propData?.totalPrice) < Number(propData?.originalPrice) ||
          propData?.pricingRules?.[0]?.value ? (
            <Badge className="absolute top-2 z-10 right-2 bg-green-main">
              {t("propCard.discount")}{" "}
              {propData?.pricingRules?.[0]?.value
                ? Math.abs(Number(propData?.pricingRules[0]?.value))
                : Math.abs(
                    100 -
                      Math.round(
                        (Number(propData?.totalPrice) /
                          Number(propData?.originalPrice)) *
                          100
                      )
                  )}{" "}
              {propData?.pricingRules?.[0]?.type === "ABSOLUTE_DIFFERENCE"
                ? t("propCard.current")
                : "%"}
            </Badge>
          ) : null}
          {image ? (
            <div className="flex shadow rounded-lg justify-center items-center h-56 w-full">
              {/* Show loader if loading */}
              {loading && !err && <CircularProgress size="lg" />}

              {/* Show warning icon if there's an error */}
              {err && <CircleAlert className="text-gray-500 size-10" />}
              <Image
                fill
                priority
                src={image}
                className={cn("rounded-lg size-full object-cover", {
                  hidden: loading || err,
                })}
                onLoad={() => setLoading(false)} // Update loading state on success
                onError={() => {
                  setErr(true); // Update error state
                  setLoading(false); // Stop the loader
                }}
                alt={propData?.title ?? "property image"}
              />
            </div>
          ) : (
            <div className="h-56 w-full bg-slate-200 rounded-lg flex justify-center items-center">
              <CircleAlert className="size-10 text-gray-500" />
            </div>
          )}
          <div className="absolute top-2 left-4">
            <FavoriteBtn {...{ id, isFavorite, isLoading, refetch }} />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="mt-6 space-y-4">
        <p className="flex gap-3 items-center max-sm:text-sm">
          <UnitIcon
            type={propData?.classification!}
            className="[&_path]:fill-gray-light size-8"
          />
          {propData?.title}
        </p>
        <div className="flex justify-between items-center gap-2 flex-wrap max-sm:text-xs">
          <span
            title={`${
              lang === "ar"
                ? cities?.find((c) => c?.id === propData?.cityId)?.arabicName
                : cities?.find((c) => c?.id === propData?.cityId)?.name
            }, ${propData?.location?.address}`}
            className="text-gray-light flex gap-1 items-center"
          >
            <MapPin />
            <span className="truncate max-w-24">
              {lang === "ar"
                ? cities?.find((c) => c?.id === propData?.cityId)?.arabicName
                : cities?.find((c) => c?.id === propData?.cityId)?.name}
            </span>
          </span>
          <div className="*:flex *:gap-1 flex gap-4 text-gray-light">
            <div>
              <DoubleBed className="size-5 max-sm:size-4" />
              {propData?.numOfBedrooms}
            </div>
            <div>
              <Image
                src={swimmingPool}
                alt="swimming pool"
                className="size-5 max-sm:size-4"
              />
              {
                new Set(
                  propData?.entertainment
                    .map((e) =>
                      e.tieredPool
                        ? "tieredPool"
                        : e.adultPool
                        ? "adultPool"
                        : e.childrenPool
                        ? "childrenPool"
                        : null
                    )
                    .filter(Boolean)
                ).size
              }
            </div>
            <div>
              <LivingRoom className="size-5 max-sm:size-4" />
              {(propData?.numOfAdditionalLivingrooms ?? 0) +
                (propData?.numOfInternalLivingrooms ?? 0) +
                (propData?.numOfExternalLivingrooms ?? 0)}
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-between items-center">
          <Badge className="flex uppercase gap-2 w-fit bg-gray-dark rounded-lg whitespace-nowrap">
            <FeedbackStar className="[&>path]:fill-white size-4" />
            {Number(propData?.overallRating) ? (
              <span>
                <span className="font-semibold">{propData?.overallRating}</span>{" "}
                {/* <span>
                  ({propData?._count?.reviews} {t("propCard.rating")})
                </span> */}
              </span>
            ) : (
              <span className="font-semibold">{t("newProp")}</span>
            )}
          </Badge>
          <p className=" flex items-center gap-2">
            {(Number(propData?.totalPrice) < Number(propData?.originalPrice) ||
              propData?.pricingRules?.[0]?.value) && (
              <del className="font-light text-xs text-gray-light">
                {roundTo1Decimal(
                  Number(propData?.originalPrice) / duration
                ).toLocaleString("en-US")}
              </del>
            )}
            <strong className="text-green-main text-lg">
              {roundTo1Decimal(
                Number(propData?.totalPrice) / duration
              ).toLocaleString("en-US")}
            </strong>
            <span className="max-sm:text-sm text-gray-dark">
              {t("propCard.perNight")}
            </span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropCard;
